import React, { useState, useEffect, Fragment, useRef, useCallback } from 'react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon, CheckIcon, UserPlusIcon, UsersIcon, BriefcaseIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import 'react-phone-number-input/style.css';
import { toast } from 'sonner';
import { createContact, assignContactToLead } from '../../../services/contactServices';
import { useContacts, invalidateContactsCache } from '../../../hooks/useContacts';
import { autocompleteContact } from '../../../services/formBuilderService';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PhoneInput, { formatPhoneNumberIntl, isValidPhoneNumber } from 'react-phone-number-input';
import { isPhoneEmpty, isPhoneEmptyOrValid, cleanPhoneNumber } from '../../../utils/phoneUtils';
import { formatABN } from '../../../utils/formatABN';
import { formatNumber } from 'utils/formatNumber';
import CustomCreatableDropdown from "custom-components/shared/CustomCreatableDropdown";
import useDropdown from "hooks/useDropdown";
import useSuburbRecommendation from "hooks/useSuburbRecommendation";
import { DropdownEnum } from "enum/DropdownEnum";
const schema = yup.object().shape({
  fullName: yup.string().required('Full Name is required'),
  phoneNumber: yup.string().transform((value, originalValue) => (isPhoneEmpty(originalValue) ? null : value)).nullable().test('is-valid-phone', 'Invalid phone number', (value) => {
    return isPhoneEmptyOrValid(value);
  }),
  email: yup.string().transform((value, originalValue) => (originalValue === '' ? null : value)).nullable().email('Invalid email address'),
  streetAddress: yup.string().nullable(),
  suburb: yup.string().nullable(),
  postalCode: yup.string().nullable(),
  state: yup.string().nullable(),
  abn: yup.string().nullable(),
  companyName: yup.string().nullable()
});

const formatPrefilledPhone = (phone) => {
  if (!phone) return "";

  // Remove all non-digit characters except the leading '+'
  let clean = phone.trim();
  const hasPlus = clean.startsWith("+");
  clean = clean.replace(/\D/g, ""); // Keep only digits

  if (hasPlus) {
    return "+" + clean;
  }

  // If it starts with 61 (Australia) and length is 11, prepend +
  if (clean.startsWith("61") && clean.length === 11) {
    return "+" + clean;
  }

  // If it starts with 91 (India) and length is 12, prepend +
  if (clean.startsWith("91") && clean.length === 12) {
    return "+" + clean;
  }

  // If it starts with 0 and length is 10 (like 0412345678)
  if (clean.startsWith("0") && clean.length === 10) {
    return "+61" + clean.slice(1);
  }

  // If it is 9 digits and starts with 4 (like 412345678)
  if (clean.length === 9 && clean.startsWith("4")) {
    return "+61" + clean;
  }

  // Default fallback for local AU number without leading 0 (e.g. 412345678)
  if (clean.length === 9) {
    return "+61" + clean;
  }

  return clean;
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function AssignContactsToLeadModal({ isOpen, onClose, leadId, initialMode = 'new', onAssigned, leadDetails, hasAssignedContacts, jobId, existingContacts, isLocalSelection = false }) {
  const [mode, setMode] = useState(initialMode); // 'new' or 'existing'
  const [isSubmitting, setIsSubmitting] = useState(false);

  const suburbDropdown = useDropdown(DropdownEnum.Suburb, isOpen);
  const stateDropdown = useDropdown(DropdownEnum.State, isOpen);
  const companyDropdown = useDropdown(DropdownEnum.Company, isOpen);

  // New Contact Form State
  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    clearErrors,
    getValues,
    setValue,
    formState: { errors, isSubmitting: isFormSubmitting }
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      fullName: '',
      email: '',
      phoneNumber: '+61',
      streetAddress: '',
      suburb: '',
      postalCode: '',
      state: '',
      abn: '',
      companyName: ''
    }
  });

  // Autofill State + PostalCode when the selected/typed Suburb matches a known
  // recommendation. Never fires for State-only entry — only Suburb resolves State/PostalCode.
  const lookupSuburbRecommendation = useSuburbRecommendation((rec) => {
    setValue("state", rec.state || "");
    setValue("postalCode", rec.postalCode || "");
  });

  // Existing Contact State
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]); // array of contact objects
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasPrefilled, setHasPrefilled] = useState(false);

  const [suggestions, setSuggestions] = useState({});
  const [showSuggestions, setShowSuggestions] = useState({});
  const [searchingField, setSearchingField] = useState({});
  const searchTimeoutRef = useRef({});

  const handleSearchContact = (fieldName, value, searchType) => {
    if (searchTimeoutRef.current[fieldName]) {
      clearTimeout(searchTimeoutRef.current[fieldName]);
    }

    const trimmed = (value || '').trim();
    if (trimmed.length < 4) {
      setSuggestions(prev => ({ ...prev, [fieldName]: [] }));
      setShowSuggestions(prev => ({ ...prev, [fieldName]: false }));
      setSearchingField(prev => ({ ...prev, [fieldName]: false }));
      return;
    }

    setSearchingField(prev => ({ ...prev, [fieldName]: true }));
    setShowSuggestions(prev => ({ ...prev, [fieldName]: true }));

    searchTimeoutRef.current[fieldName] = setTimeout(async () => {
      try {
        const response = await autocompleteContact(trimmed, searchType);
        if (response.success && response.data && response.data.length > 0) {
          setSuggestions(prev => ({ ...prev, [fieldName]: response.data }));
          setShowSuggestions(prev => ({ ...prev, [fieldName]: true }));
        } else {
          setShowSuggestions(prev => ({ ...prev, [fieldName]: false }));
        }
      } catch (err) {
        console.error("Autocomplete error:", err);
        setShowSuggestions(prev => ({ ...prev, [fieldName]: false }));
      } finally {
        setSearchingField(prev => ({ ...prev, [fieldName]: false }));
      }
    }, 400);
  };

  const handleSelectContactSuggestion = (fieldName, contact) => {
    reset({
        ...getValues(),
        fullName: contact.fullName || getValues('fullName'),
        email: contact.email || getValues('email'),
        phoneNumber: (contact.phoneNumber && !String(contact.phoneNumber).startsWith('+') && !String(contact.phoneNumber).startsWith('0')) ? `+61${contact.phoneNumber}` : (contact.phoneNumber || getValues('phoneNumber')),
        streetAddress: contact.streetAddress || getValues('streetAddress'),
        suburb: contact.suburb || getValues('suburb'),
        postalCode: contact.postalCode || contact.postcode || getValues('postalCode'),
        state: contact.state || getValues('state'),
        companyName: contact.companyName || contact.company_name || getValues('companyName'),
        abn: contact.abnNumber || contact.abn || contact.Abn || getValues('abn')
    });
    
    clearErrors(['fullName', 'email', 'phoneNumber']);
    
    setShowSuggestions({
        fullName: false,
        email: false,
        phoneNumber: false
    });
  };

  const renderSuggestionsDropdown = (fieldName) => {
    const isSearching = searchingField[fieldName];
    const hasSuggestions = suggestions[fieldName] && suggestions[fieldName].length > 0;
    const isOpen = showSuggestions[fieldName];

    if (!isSearching && !hasSuggestions) return null;

    return (
      <div
        className={`absolute z-[80] top-full left-0 right-0 mt-1 bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg shadow-xl max-h-60 overflow-y-auto divide-y divide-gray-100 dark:divide-dark-600 transition-all duration-200 ease-in-out origin-top transform ${isOpen ? "opacity-100 translate-y-0 scale-100 pointer-events-auto" : "opacity-0 -translate-y-2 scale-95 pointer-events-none"}`}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {isSearching ? (
          <div className="flex items-center justify-center gap-2.5 px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
            <svg className="animate-spin h-4 w-4 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            <span>Searching contacts...</span>
          </div>
        ) : (
          suggestions[fieldName].map((contact) => (
            <div
              key={contact.contactId || contact.id}
              className="p-3 hover:bg-primary-50 dark:hover:bg-primary-900/30 cursor-pointer text-left transition-colors"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelectContactSuggestion(fieldName, contact);
              }}
            >
              <div className="font-semibold text-sm text-gray-800 dark:text-white truncate">{contact.fullName}</div>
              <div className="flex flex-col gap-0.5 mt-1 text-xs text-gray-500 dark:text-gray-400">
                {contact.phoneNumber && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400">📞</span>
                    <span>{contact.phoneNumber}</span>
                  </div>
                )}
                {contact.email && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400">✉️</span>
                    <span className="truncate">{contact.email}</span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  // Fetch existing contacts for search (only when modal is open and on existing contact mode)
  const { contacts: allContacts, isLoading: isSearching } = useContacts((isOpen && mode === 'existing') ? null : false, debouncedSearch);

  // Fetch contacts assigned to this lead (only when modal is open)
  const { contacts: leadContacts, isLoading: isLeadContactsLoading } = useContacts((isOpen && leadId) ? leadId : false, '');

  const isCurrentlySearching = isSearching || search !== debouncedSearch;

  useEffect(() => {
    if (isOpen) {
      if (!hasPrefilled) {
        setMode(initialMode);

        reset({
          fullName: '',
          email: '',
          phoneNumber: '+61',
          streetAddress: '',
          suburb: '',
          postalCode: '',
          state: '',
          abn: '',
          companyName: ''
        });
        clearErrors();

        setSearch('');
        setIsSubmitting(false);
        setHasPrefilled(true);
      }
    } else {
      setHasInitialized(false);
      setHasPrefilled(false);
      setSelectedContacts([]);
      reset({
        fullName: '',
        email: '',
        phoneNumber: '+61',
        streetAddress: '',
        suburb: '',
        postalCode: '',
        state: '',
        abn: '',
        companyName: ''
      });
      clearErrors();
    }
  }, [isOpen, initialMode, hasAssignedContacts, leadDetails, hasPrefilled, reset, clearErrors]);

  useEffect(() => {
    if (isOpen) {
      if (!hasInitialized) {
        if (existingContacts) {
          setSelectedContacts(existingContacts);
          setHasInitialized(true);
        } else if (!isLeadContactsLoading) {
          setSelectedContacts(leadContacts || []);
          setHasInitialized(true);
        }
      }
    }
  }, [isOpen, leadContacts, isLeadContactsLoading, hasInitialized, existingContacts]);

  const onSubmitNew = async (data) => {
    const cleanAbn = data.abn ? data.abn.toString().replace(/\s+/g, '') : null;
    const cleanPhone = cleanPhoneNumber(data.phoneNumber);
    const cleanEmail = (data.email && data.email.trim() !== '') ? data.email.trim() : null;

    if (!cleanPhone && !cleanEmail) {
      setError('email', { type: 'manual' });
      setError('phoneNumber', { type: 'manual' });
      toast.error("At least email or phone number is required");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await createContact({
        fullName: data.fullName,
        email: cleanEmail,
        phoneNumber: cleanPhone,
        leadId: leadId || null,
        city: null,
        postalCode: data.postalCode || null,
        state: data.state || null,
        suburb: data.suburb || null,
        streetAddress: data.streetAddress || null,
        companyName: data.companyName || null,
        abnNumber: cleanAbn ? Number(cleanAbn) : null
      });

      if (!res.success) {
        if (res.error && res.error.toLowerCase().includes('email or phone')) {
          setError('email', { type: 'manual' });
          setError('phoneNumber', { type: 'manual' });
        }
        toast.error(res.error || "Failed to create contact");
        setIsSubmitting(false);
        return;
      }

      // Reset form fields
      reset({
        fullName: '',
        email: '',
        phoneNumber: '+61',
        streetAddress: '',
        suburb: '',
        postalCode: '',
        state: '',
        abn: '',
        companyName: ''
      });

      // Force revalidation of all contacts queries
      invalidateContactsCache();

      // Notify parent about new assignment
      if (onAssigned) onAssigned();

      // Close the modal as requested
      onClose();

      toast.success((jobId || isLocalSelection) ? "Contact created and added successfully" : "Contact created and assigned successfully");
    } catch (error) {
      console.error("Error creating/assigning new contact:", error);
      toast.error(((jobId || isLocalSelection) ? "Failed to create and add client: " : "Failed to create and assign contact: ") + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignExisting = async () => {
    if (selectedContacts.length === 0) return;

    if (isLocalSelection) {
      if (onAssigned) onAssigned(selectedContacts);
      onClose();
      return;
    }

    setIsSubmitting(true);
    try {
      const contactIds = Array.from(new Set(selectedContacts.map(c => c.id || c.ContactId || c.Id)));
      const res = await assignContactToLead(leadId, contactIds, jobId);

      if (!res.success) {
        toast.error(res.error || ((jobId || isLocalSelection) ? "Failed to add clients" : "Failed to assign contacts"));
        setIsSubmitting(false);
        return;
      }

      if (leadId) invalidateContactsCache(leadId); // Refresh lead's contacts
      if (jobId) invalidateContactsCache(jobId);

      if (onAssigned) onAssigned(selectedContacts);
      onClose();
    } catch (error) {
      console.error("Error assigning contacts:", error);
      toast.error(((jobId || isLocalSelection) ? "Failed to add clients: " : "Failed to assign contacts: ") + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleContactSelection = (contact) => {
    const contactId = contact.id || contact.ContactId || contact.Id;
    if (selectedContacts.some(c => String(c.id || c.ContactId || c.Id || '') === String(contactId || ''))) {
      setSelectedContacts(selectedContacts.filter(c => String(c.id || c.ContactId || c.Id || '') !== String(contactId || '')));
    } else {
      setSelectedContacts([...selectedContacts, contact]);
    }
  };

  const removeSelectedContact = (contactId) => {
    setSelectedContacts(selectedContacts.filter(c => String(c.id || c.ContactId || c.Id || '') !== String(contactId || '')));
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-xl transform flex flex-col overflow-hidden max-h-[90vh] rounded-2xl bg-white dark:bg-dark-800 p-6 text-left align-middle shadow-xl transition-all border border-gray-100 dark:border-dark-700">
                <div className="flex justify-between items-center mb-5 shrink-0">
                  <Dialog.Title as="h3" className="text-lg font-bold leading-6 text-gray-900 dark:text-white">
                    {(jobId || isLocalSelection) ? 'Add Clients' : 'Add Contact to Lead'}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="rounded-full p-1 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                <Tab.Group as="div" className="flex flex-col flex-1 min-h-0" selectedIndex={mode === 'new' ? 0 : 1} onChange={(index) => setMode(index === 0 ? 'new' : 'existing')}>

                  <Tab.Panels className="mt-2 flex-1 overflow-hidden flex flex-col min-h-0">
                    {/* New Contact Form */}
                    <Tab.Panel className="focus:outline-none flex-1 overflow-hidden flex flex-col min-h-0">
                      <form onSubmit={handleSubmit(onSubmitNew)} className="flex flex-col flex-1 overflow-hidden min-h-0">
                        <div className="space-y-4 overflow-y-auto px-1 -mx-1 pb-1 flex-1 min-h-0 max-h-[calc(90vh-160px)]">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Full Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                {...register('fullName', {
                                  onChange: (e) => {
                                    handleSearchContact('fullName', e.target.value, 'name');
                                    if (errors.fullName) clearErrors('fullName');
                                  }
                                })}
                                onBlur={() => setShowSuggestions(prev => ({ ...prev, fullName: false }))}
                                className={`w-full rounded-lg border bg-white dark:bg-dark-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none transition-colors ${
                                  errors.fullName
                                    ? 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                                    : 'border-gray-300 dark:border-dark-600 focus:border-primary-500 focus:ring-1 focus:ring-primary-500'
                                }`}
                                placeholder="John Doe"
                              />
                              {renderSuggestionsDropdown('fullName')}
                            </div>
                            {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName.message}</p>}
                          </div>
                          
                         <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Phone Number
                            </label>
                            <Controller
                              name="phoneNumber"
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <div className="relative">
                                  <div className={`relative rounded-lg border transition-all duration-200 bg-white dark:bg-dark-900 ${errors.phoneNumber
                                      ? 'border-red-500 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500'
                                      : 'border-gray-300 dark:border-dark-600 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500'
                                    }`}>
                                    <PhoneInput
                                      international
                                      defaultCountry="AU"
                                      addInternationalOption={true}
                                      countryCallingCodeEditable={false}
                                      value={value}
                                      countrySelectProps={{ tabIndex: -1 }}
                                      onChange={(val) => {
                                        onChange(val);
                                        handleSearchContact('phoneNumber', val, 'phone');
                                        if (errors.phoneNumber || errors.email) {
                                          clearErrors(['phoneNumber', 'email']);
                                        }
                                      }}
                                      onBlur={() => setShowSuggestions(prev => ({ ...prev, phoneNumber: false }))}
                                      className="px-3 py-2 w-full"
                                      numberInputProps={{
                                        className: "w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none",
                                        required: false,
                                        onFocus: (e) => {
                                          const target = e.target;
                                          setTimeout(() => {
                                            if (target) {
                                              const valLen = target.value.length;
                                              target.setSelectionRange(valLen, valLen);
                                            }
                                          }, 10);
                                        },
                                        onKeyUp: (e) => {
                                          if (e.key === 'Tab') {
                                            const valLen = e.target.value.length;
                                            e.target.setSelectionRange(valLen, valLen);
                                          }
                                        }
                                      }}
                                    />
                                  </div>
                                  {renderSuggestionsDropdown('phoneNumber')}
                                </div>
                              )}
                            />
                            {errors.phoneNumber && <p className="text-red-500 text-xs mt-1">{errors.phoneNumber.message}</p>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Email Address
                            </label>
                            <div className="relative">
                              <input
                                type="email"
                                {...register('email', {
                                  onChange: (e) => {
                                    handleSearchContact('email', e.target.value, 'email');
                                    if (errors.email || errors.phoneNumber) {
                                      clearErrors(['email', 'phoneNumber']);
                                    }
                                  }
                                })}
                                onBlur={() => setShowSuggestions(prev => ({ ...prev, email: false }))}
                                className={`w-full rounded-lg border bg-white dark:bg-dark-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none transition-colors ${
                                  errors.email
                                    ? 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                                    : 'border-gray-300 dark:border-dark-600 focus:border-primary-500 focus:ring-1 focus:ring-primary-500'
                                }`}
                                placeholder="john@example.com"
                              />
                              {renderSuggestionsDropdown('email')}
                            </div>
                            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Company Name 
                            </label>
                            <Controller
                              name="companyName"
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <CustomCreatableDropdown
                                  height="38px"
                                  minHeight="38px"
                                  options={companyDropdown.options}
                                  value={value ? (companyDropdown.options.find(opt => opt.label === value) || { label: value, value: value }) : null}
                                  onChange={(val) => onChange(val ? val.label : "")}
                                  onCreateOption={async (val) => {
                                    const res = await companyDropdown.onCreateOption(val);
                                    if (res && res.success) onChange(val);
                                    return res;
                                  }}
                                  onEditOption={async (id, currentLabel, newText) => {
                                    const success = await companyDropdown.onEditOption(id, currentLabel, newText);
                                    if (success && value === currentLabel) onChange(newText);
                                    return success;
                                  }}
                                  onDeleteOption={async (id, label) => {
                                    const success = await companyDropdown.onDeleteOption(id, label);
                                    if (success && value === label) onChange("");
                                    return success;
                                  }}
                                  isLoading={companyDropdown.isLoading}
                                  placeholder="Company Name"
                                  error={!!errors.companyName}
                                />
                              )}
                            />
                            {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName.message}</p>}
                          </div>
                           
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              ABN 
                            </label>
                            <input
                              type="text"
                              {...register('abn', {
                                onChange: (e) => {
                                  e.target.value = formatABN(e.target.value);
                                }
                              })}
                              className={`w-full rounded-lg border ${errors.abn ? 'border-red-500' : 'border-gray-300 dark:border-dark-600'} bg-white dark:bg-dark-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 transition-colors`}
                              placeholder="ABN"
                            />
                            {errors.abn && <p className="text-red-500 text-xs mt-1">{errors.abn.message}</p>}
                          </div>
                        </div>

                        <div className="pt-2 border-t border-gray-100 dark:border-dark-700 space-y-4">
                          <label className="block text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                            Address Details
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Street Address
                              </label>
                              <input
                                type="text"
                                {...register('streetAddress')}
                                className="w-full rounded-lg border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 transition-colors"
                                placeholder="123 Example St"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Suburb
                              </label>
                              <Controller
                                name="suburb"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <CustomCreatableDropdown
                                    height="38px"
                                    minHeight="38px"
                                    options={suburbDropdown.options}
                                    value={value ? (suburbDropdown.options.find(opt => opt.label === value) || { label: value, value: value }) : null}
                                    onChange={(val) => {
                                      const label = val ? val.label : "";
                                      onChange(label);
                                      lookupSuburbRecommendation(label);
                                    }}
                                    onCreateOption={async (val) => {
                                      const res = await suburbDropdown.onCreateOption(val);
                                      if (res && res.success) onChange(val);
                                      return res;
                                    }}
                                    onEditOption={async (id, currentLabel, newText) => {
                                      const success = await suburbDropdown.onEditOption(id, currentLabel, newText);
                                      if (success && value === currentLabel) onChange(newText);
                                      return success;
                                    }}
                                    onDeleteOption={async (id, label) => {
                                      const success = await suburbDropdown.onDeleteOption(id, label);
                                      if (success && value === label) onChange("");
                                      return success;
                                    }}
                                    isLoading={suburbDropdown.isLoading}
                                    placeholder="Suburb"
                                  />
                                )}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Postal Code
                              </label>
                              <input
                                type="text"
                                {...register('postalCode')}
                                className="w-full rounded-lg border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-900 px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 transition-colors"
                                placeholder="Postal Code"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                State
                              </label>
                              <Controller
                                name="state"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <CustomCreatableDropdown
                                    height="38px"
                                    minHeight="38px"
                                    options={stateDropdown.options}
                                    value={value ? (stateDropdown.options.find(opt => opt.label === value) || { label: value, value: value }) : null}
                                    onChange={(val) => onChange(val ? val.label : "")}
                                    onCreateOption={async (val) => {
                                      const res = await stateDropdown.onCreateOption(val);
                                      if (res && res.success) onChange(val);
                                      return res;
                                    }}
                                    onEditOption={async (id, currentLabel, newText) => {
                                      const success = await stateDropdown.onEditOption(id, currentLabel, newText);
                                      if (success && value === currentLabel) onChange(newText);
                                      return success;
                                    }}
                                    onDeleteOption={async (id, label) => {
                                      const success = await stateDropdown.onDeleteOption(id, label);
                                      if (success && value === label) onChange("");
                                      return success;
                                    }}
                                    isLoading={stateDropdown.isLoading}
                                    placeholder="State"
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </div>
                        </div>

                        <div className="mt-4 flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-dark-700 shrink-0">
                          <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-dark-800 dark:text-gray-300 dark:border-dark-600 dark:hover:bg-dark-700 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={isSubmitting || isFormSubmitting}
                            className="rounded-lg px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                          >
                            {isSubmitting || isFormSubmitting ? 'Saving...' : ((jobId || isLocalSelection) ? 'Create & Add' : 'Create & Assign')}
                          </button>
                        </div>
                      </form>
                    </Tab.Panel>

                    {/* Existing Contact Form */}
                    <Tab.Panel className="focus:outline-none flex-1 overflow-hidden flex flex-col min-h-0">
                      <div className="flex flex-col flex-1 overflow-hidden min-h-0">
                        <div className="space-y-4 overflow-y-auto px-1 -mx-1 pb-1 flex-1 min-h-0 max-h-[calc(90vh-160px)]">
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                          </div>
                          <input
                           autoFocus
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="block w-full rounded-lg border border-gray-300 dark:border-dark-600 bg-gray-50 dark:bg-dark-900/50 py-2.5 pl-10 pr-3 text-sm text-gray-900 dark:text-white placeholder:text-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:bg-white dark:focus:bg-dark-900 transition-all"
                            placeholder={(jobId || isLocalSelection) ? "Search clients by name, email, or phone..." : "Search contacts by name, email, or phone..."}
                          />
                        </div>

                        {/* Selected Contacts Chips */}
                        {selectedContacts.length > 0 && (
                          <div className="flex flex-wrap gap-2 pt-1 pb-2">
                            {selectedContacts.map((contact) => (
                              <div
                                key={contact.id || contact.ContactId || contact.Id}
                                className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 dark:bg-primary-900/20 px-2.5 py-1 text-xs font-medium text-primary-700 dark:text-primary-300 border border-primary-100 dark:border-primary-800/30"
                              >
                                {contact.FullName || contact.fullName || contact.name}
                                <button
                                  type="button"
                                  onClick={() => removeSelectedContact(contact.id || contact.ContactId || contact.Id)}
                                  className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-primary-400 hover:bg-primary-200 hover:text-primary-500 dark:hover:bg-primary-800 dark:hover:text-primary-200 transition-colors focus:bg-primary-500 focus:text-white focus:outline-none"
                                >
                                  <XMarkIcon className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="mt-4 border border-gray-200 dark:border-dark-700 rounded-xl overflow-hidden bg-white dark:bg-dark-800 flex flex-col h-[280px]">
                          {isCurrentlySearching ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"></div>
                            </div>
                          ) : allContacts.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-gray-500 p-6 text-center">
                              <UsersIcon className="w-10 h-10 mb-2 opacity-20" />
                              <p className="text-sm font-medium">No contacts found</p>
                              {search && <p className="text-xs mt-1 text-gray-400">Try adjusting your search</p>}
                            </div>
                          ) : (
                            <div className="overflow-y-auto flex-1 p-2 space-y-1">
                              {[...allContacts].sort((a, b) => {
                                const nameA = (a.FullName || a.fullName || a.name || "").toLowerCase();
                                const nameB = (b.FullName || b.fullName || b.name || "").toLowerCase();
                                return nameA.localeCompare(nameB);
                              }).map((contact) => {
                                const contactId = contact.id || contact.ContactId || contact.Id;
                                const isSelected = selectedContacts.some(c => String(c.id || c.ContactId || c.Id || '') === String(contactId || ''));

                                return (
                                  <div
                                    key={contactId}
                                    onClick={() => toggleContactSelection(contact)}
                                    className={classNames(
                                      "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border border-transparent",
                                      isSelected
                                        ? "bg-primary-50/50 dark:bg-primary-900/10 border-primary-100 dark:border-primary-900/30"
                                        : "hover:bg-gray-50 dark:hover:bg-dark-700/50"
                                    )}
                                  >
                                    <div className={classNames(
                                      "flex-shrink-0 w-5 h-5 rounded flex items-center justify-center transition-colors border",
                                      isSelected
                                        ? "bg-primary-600 border-primary-600 text-white"
                                        : "bg-white dark:bg-dark-900 border-gray-300 dark:border-dark-600 text-transparent"
                                    )}>
                                      <CheckIcon className="w-3.5 h-3.5" strokeWidth={3} />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {contact.FullName || contact.fullName || contact.name || "Unknown"}
                                      </div>
                                      {(contact.PhoneNumber || contact.Email || contact.email) && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                          {formatNumber(contact.PhoneNumber)} {contact.PhoneNumber && contact.Email && '•'} {contact.Email || contact.email}
                                        </div>
                                      )}
                                      {(() => {
                                        const comp = contact.CompanyName || contact.companyName || contact.company_name;
                                        const abnVal = contact.ABNNumber || contact.abnNumber || contact.abn || contact.Abn;
                                        if (!comp && !abnVal) return null;
                                        return (
                                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                                            {comp && (
                                              <span className="flex items-center gap-1">
                                                <BriefcaseIcon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                                <span className="truncate">{comp}</span>
                                              </span>
                                            )}
                                            {comp && abnVal && <span className="text-gray-300 dark:text-gray-650">•</span>}
                                            {abnVal && (
                                              <span className="flex items-center gap-1">
                                                <DocumentTextIcon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                                <span>{formatABN(abnVal)}</span>
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })()}
                                      {(() => {
                                        const street = contact.streetAddress || contact.StreetAddress;
                                        const suburb = contact.suburb || contact.Suburb;
                                        const postalCode = contact.postalCode || contact.PostalCode || contact.postcode;
                                        const state = contact.state || contact.State;
                                        const address = [street, suburb, postalCode, state].filter(Boolean).join(", ");
                                        return address ? (
                                          <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
                                            📍 {address}
                                          </div>
                                        ) : null;
                                      })()}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        </div>

                        <div className="mt-4 flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-dark-700 shrink-0">
                          <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-dark-800 dark:text-gray-300 dark:border-dark-600 dark:hover:bg-dark-700 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleAssignExisting}
                            disabled={isSubmitting || selectedContacts.length === 0}
                            className="rounded-lg px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                          >
                            {isSubmitting 
                              ? ((jobId || isLocalSelection) ? 'Adding...' : 'Assigning...') 
                              : ((jobId || isLocalSelection) ? `Add Selected (${selectedContacts.length})` : `Assign Selected (${selectedContacts.length})`)}
                          </button>
                        </div>
                      </div>
                    </Tab.Panel>
                  </Tab.Panels>
                </Tab.Group>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
